import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { readShigoAppConfig } from '../../../../../lib/config';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import type { createCampaignsClient } from '../../api';
import {
  mapMadBuyLineItemAdTypeToCreativeType,
  type MadBuyCreativeAssociation,
  type MadBuyLineItemType,
} from '../../lineItemsModel';
import { dashboardT } from '../../../../../lib/messages';
import { FormField } from './LineItemEditorFields';
import {
  resolveCreativeSelectHelperMessage,
  resolveSelectedCreative,
} from './CreativeAssociationDialog.helpers';

type MadBuyCampaignsClient = ReturnType<typeof createCampaignsClient>;

const lineItemEditorLookupQueryOptions = {
  staleTime: 10 * 60 * 1000,
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const;

type CreativeAssociationDialogProps = {
  adType: MadBuyLineItemType;
  alreadySelectedIds: string[];
  association?: MadBuyCreativeAssociation | undefined;
  brandId: string;
  client: MadBuyCampaignsClient;
  onOpenChange: (open: boolean) => void;
  onSave: (association: MadBuyCreativeAssociation) => void;
  open: boolean;
};

export function CreativeAssociationDialog({
  adType,
  alreadySelectedIds,
  association,
  brandId,
  client,
  onOpenChange,
  onSave,
  open,
}: CreativeAssociationDialogProps) {
  const scope = useAgencyCampaignContext();
  const [creativeId, setCreativeId] = useState(association?.creative.id ?? '');
  const [weight, setWeight] = useState(association ? String(association.weight) : '');
  const creativeType = mapMadBuyLineItemAdTypeToCreativeType(adType);
  const creativesQuery = useQuery({
    queryKey: [
      ...(scope ? ['agency-demand', readShigoAppConfig().networkId, scope.agency.id] : []),
      'mad-buy-creatives',
      brandId,
      creativeType,
    ],
    enabled: open && Boolean(brandId),
    queryFn: () => client.listCreatives({ brandId, type: creativeType, page: 0, size: 9999 }),
    ...lineItemEditorLookupQueryOptions,
  });

  const selectedCreativeIds = new Set(alreadySelectedIds);
  const availableCreatives = (creativesQuery.data ?? []).filter(
    (creative) => creative.id === association?.creative.id || !selectedCreativeIds.has(creative.id),
  );
  const selectedCreative = resolveSelectedCreative({
    associationCreative: association?.creative,
    availableCreatives,
    creativeId,
  });
  const creativeSelectPlaceholder = resolveCreativeSelectPlaceholder({
    brandSelected: Boolean(brandId),
    loading: creativesQuery.isLoading,
  });
  const creativeSelectHelperMessage = resolveCreativeSelectHelperMessage({
    availableCount: availableCreatives.length,
    brandSelected: Boolean(brandId),
    error: creativesQuery.isError,
    loading: creativesQuery.isLoading,
  });
  const trimmedWeight = weight.trim();
  const numericWeight = Number(trimmedWeight);
  const canSave =
    Boolean(selectedCreative) &&
    trimmedWeight.length > 0 &&
    Number.isFinite(numericWeight) &&
    numericWeight >= 0 &&
    numericWeight <= 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card max-w-[560px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle>
            {association
              ? dashboardT('madBuy.lineItems.editCreativeAssociation')
              : dashboardT('madBuy.lineItems.newCreativeAssociation')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 px-5 py-5">
          <FormField label={dashboardT('madBuy.lineItems.creative')} required>
            <Select
              value={creativeId || 'none'}
              disabled={!brandId || creativesQuery.isLoading}
              onValueChange={setCreativeId}
            >
              <SelectTrigger aria-label={dashboardT('madBuy.lineItems.creative')}>
                <SelectValue placeholder={creativeSelectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {dashboardT('madBuy.lineItems.selectCreative')}
                </SelectItem>
                {availableCreatives.map((creative) => (
                  <SelectItem key={creative.id} value={creative.id}>
                    {creative.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {creativeSelectHelperMessage ? (
              <p className="text-muted-foreground text-sm">{creativeSelectHelperMessage}</p>
            ) : null}
          </FormField>
          <FormField label={dashboardT('madBuy.lineItems.weight')} required>
            <Input
              aria-label={dashboardT('madBuy.lineItems.weight')}
              inputMode="decimal"
              value={weight}
              onChange={(event) => {
                setWeight(event.target.value);
              }}
            />
          </FormField>
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {dashboardT('madBuy.common.cancel')}
          </Button>
          <Button
            type="button"
            className="rounded-full"
            disabled={!canSave || !selectedCreative}
            onClick={() => {
              if (!selectedCreative) {
                return;
              }
              onSave({
                id: association?.id,
                creative: selectedCreative,
                weight: numericWeight,
                startDate: association?.startDate,
                endDate: association?.endDate,
              });
            }}
          >
            {dashboardT('madBuy.common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function resolveCreativeSelectPlaceholder({
  brandSelected,
  loading,
}: {
  brandSelected: boolean;
  loading: boolean;
}) {
  if (!brandSelected) {
    return dashboardT('madBuy.lineItems.selectBrandFirst');
  }

  return loading
    ? dashboardT('madBuy.creatives.loading')
    : dashboardT('madBuy.lineItems.selectCreative');
}
